import React, { useEffect } from 'react';
import classNames from 'classnames';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GridContainer } from '@pec/aion-ui-components/components/GridContainer';
import { CircularProgress } from '@pec/aion-ui-components/components/CircularProgress';
import { localizeNumber } from '@pec/aion-ui-i18next/helpers/localize';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IAddScoreResult } from 'interfaces/scoreResult';
import { fetchReleasesIfNeeded } from 'actions/releases';
import { fetchScoreResultsIfNeeded } from 'actions/scoreResults';
import { toggleClientScoresTileExpandedOrganizationId } from 'actions/options';
import { useTypedSelector, useAppDispatch } from '../../combineReducers';
import { translateScoreResult } from 'helpers/translateScoreResult';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      minWidth: 400,
      marginBottom: theme.spacing(2)
    },
    loaderCard: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 400
    },
    title: {
      fontWeight: 500,
      margin: `0 ${theme.spacing(1)}px`
    },
    accordionDetails: {
      padding: `${theme.spacing()}px 0px`
    },
    chip: {
      color: 'var(--on-primary-high-emphasis-ffffff)',
      fontSize: 11,
      fontWeight: 600,
      marginRight: theme.spacing(3)
    },
    accordion: {
      borderTop: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        marginBottom: `-${theme.spacing(1.5)}px`
      },
      '& .MuiAccordionSummary-root': {
        padding: `0 ${theme.spacing(1)}px`
      }
    },
    firstAccordion: {
      borderTop: 'none',
      '&.MuiAccordion-root:before': {
        backgroundColor: theme.palette.common.white
      }
    },
    table: {
      border: `1px solid ${theme.palette.divider}`,
      borderCollapse: 'initial',
      borderRadius: 4,
      '& td': {
        fontSize: 12,
        padding: theme.spacing(1),
        paddingLeft: theme.spacing(2)
      },
      '& th': {
        fontSize: 12,
        fontWeight: 600,
        padding: theme.spacing(1),
        paddingLeft: theme.spacing(2)
      }
    },
    statusIndicator: {
      borderRadius: 2,
      height: 16,
      width: 16
    }
  })
);

type RouteParams = {
  organizationId: string;
};

export const ClientScores: React.FC = () => {
  const { organizationId } = useParams<RouteParams>();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { releases, isFetching: isReleaseFetching } = useTypedSelector(state => state.releases);
  const { scoreResults, isFetching: isScoreFetching } = useTypedSelector(state => state.scoreResults);
  const { t, i18n } = useTranslation();
  const language = i18n.language.split('-')[0];
  const companyProfileLinkProps = { href: `/organizations/${organizationId}/profile` };
  const releasesLinkProps = { href: `/organizations/${organizationId}/my-connections` };

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchReleasesIfNeeded(organizationId));
    }
  }, [organizationId, dispatch]);

  useEffect(() => {
    if (organizationId && releases && releases.length > 0) {
      dispatch(fetchScoreResultsIfNeeded(organizationId));
    }
  }, [organizationId, releases, dispatch]);

  const handleAccordionTitleClick = (clientId: string) => () => {
    dispatch(toggleClientScoresTileExpandedOrganizationId(clientId));
  };

  const sortedScoreResults = React.useMemo(() => {
    const results = scoreResults && Array.isArray(scoreResults) ? [...scoreResults] : [];

    return results
      .sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''))
      .map(scoreResult => translateScoreResult(scoreResult, language));
  }, [scoreResults, language]);

  if (isReleaseFetching || isScoreFetching) {
    return (
      <Card className={classes.loaderCard}>
        <CircularProgress />
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h6" className={classes.title}>
          {t('subscriber:dashboard.clientScoresTile.title', 'Client Scores')}
        </Typography>
        {sortedScoreResults && sortedScoreResults.length > 0 ? (
          sortedScoreResults.map((scoreResult, index) => (
            <Accordion
              elevation={0}
              key={scoreResult.clientId}
              expanded={scoreResult.isExpanded}
              className={classNames(classes.accordion, {
                [classes.firstAccordion]: index < 1
              })}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                onClick={handleAccordionTitleClick(scoreResult.clientId)}
              >
                <GridContainer spacing={0} justify="space-between" alignItems="center">
                  <Grid item>
                    <Typography variant="subtitle1">{scoreResult.clientName}</Typography>
                  </Grid>
                  {scoreResult.overallRanking && (
                    <Grid item>
                      <Chip
                        label={scoreResult.overallRanking.description}
                        style={{ backgroundColor: scoreResult.overallRanking.color }}
                        className={classes.chip}
                      />
                    </Grid>
                  )}
                </GridContainer>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <TableContainer>
                  <Table className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('subscriber:dashboard.clientScoresTile.scoreSet', 'Score Set')}</TableCell>
                        <TableCell>{t('subscriber:dashboard.clientScoresTile.status', 'Status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scoreResult?.scores
                        .filter((score: IAddScoreResult) => score.isActive)
                        .sort((a: IAddScoreResult, b: IAddScoreResult) => {
                          return a.scoreSetName.localeCompare(b.scoreSetName);
                        })
                        .map((score: IAddScoreResult) => (
                          <TableRow key={score.id}>
                            <TableCell>
                              <Typography variant="inherit">{score.scoreSetName || ''}</Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gridGap={8}>
                                <Box
                                  className={classes.statusIndicator}
                                  style={{
                                    backgroundColor: score.rankingColor
                                  }}
                                />
                                <Typography variant="inherit">
                                  {score.rankingDescription}
                                  {score.calculatedScore > 0 &&
                                    ` - ${localizeNumber(
                                      {
                                        value: score.calculatedScore,
                                        config: {
                                          style: 'percent',
                                          maximumFractionDigits: 0
                                        }
                                      },
                                      t
                                    )}`}
                                </Typography>
                                {score.scoreOverride && (
                                  <Box style={{ marginLeft: 'auto' }}>
                                    <Tooltip
                                      placement="top"
                                      enterTouchDelay={0}
                                      title={
                                        t(
                                          'subscriber:dashboard.clientScoresTile.overrideApplied',
                                          'Override applied'
                                        ) as string
                                      }
                                    >
                                      <ReportProblemOutlinedIcon
                                        fontSize="small"
                                        color="primary"
                                        style={{ flexShrink: 0, display: 'block' }}
                                      />
                                    </Tooltip>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <GridContainer>
            <Grid item xs={12}>
              <Typography variant="h6">
                {t('subscriber:dashboard.clientScoresTile.noClientScores', 'No Client Scores are Available')}
              </Typography>
              <Typography variant="body1">
                <Trans i18nKey="subscriber:dashboard.clientScoresTile.reminder">
                  Please ensure your{' '}
                  <Link underline="always" {...companyProfileLinkProps}>
                    Company Profile
                  </Link>{' '}
                  is completed and your{' '}
                  <Link underline="always" {...releasesLinkProps}>
                    Releases
                  </Link>{' '}
                  are up-to-date.
                </Trans>
              </Typography>
            </Grid>
          </GridContainer>
        )}
      </CardContent>
    </Card>
  );
};
